<template>
  <div
    class=""
    :class="{'application-apartments--interactive': interactive}"
  >

    <ApplicationSection>
      <template #title>
        {{ apartmentTypeSearchQueryValue === 'owner-occupancy' ? translate('Valittu asunto') : translate('Valitut asunnot')}}<br>
        <span style="display: none;">{{count(apartmentsAvailable)}}</span>
      </template>

      <LoadSpinner v-if="loading"/>

      <div v-if="!loading">
        <div v-for="(apartments, localityName) in apartmentsGroupedByAddress" :key="localityName" style="margin-bottom: 24px;">
          <div class="apartment-grouped-by-address__container">
            <div class="apartment-grouped-by-address--title">{{ localityName }} </div>
            <div
              class="apartment-grouped-by-address--checkbox input-wrapper"
              v-if="apartmentTypeSearchQueryValue === 'right-of-occupancy'">
              <input :id="localityName" class="input-checkbox__input form-check-input" type="checkbox" :value="localityName" @change="handleCheckboxChange(localityName, apartments.locality_id)" v-model="residentInLocation.localityName" />
              <label :for="localityName" class="input-checkbox__input form-check-label"> {{translate('Asun tässä kohteessa')}}</label>
            </div>
          </div>
          <ApplicationItemsGrid
            v-if="!loading"
            :items="apartments.available"
            v-slot="{item: apartment}"
          >
            <ApplicationApartmentCard
              :apartment="apartment"
              :interactive="interactive"
              @remove="$emit('remove', apartment.mpc_asuntoid)"
            />
          </ApplicationItemsGrid>

          <template v-if="apartments.unavailable.length > 0">
            <div style="font-size: 14px; line-height: 22px; font-weight: bold; margin: 22px 0 14px;"> {{translate('Kohteesta valitut asunnot, jotka juuri nyt eivät ole vapaana')}}</div>
            <ApplicationItemsGrid
              v-if="!loading"
              :items="apartments.unavailable"
              v-slot="{item: apartment}"
            >
              <ApplicationApartmentCard
                :apartment="apartment"
                :interactive="interactive"
                @remove="$emit('remove', apartment.mpc_asuntoid)"
              />
            </ApplicationItemsGrid>
          </template>


          <ApartmentsTable
            v-if="apartmentTypeSearchQueryValue !== 'owner-occupancy'"
            :housingLocality="apartments.locality_id"
            :localityName="localityName"
          />

          <ApplicationDivider wrapperClasses="" v-if="apartmentTypeSearchQueryValue !== 'owner-occupancy'" />

        </div>
      </div>

    </ApplicationSection>

    <ApplicationSection v-if="warningApartmentsAfter" wrapperClasses="mb-5">
      <div
        class="wysiwyg warning-text print-hide"
        :class="`border--${color}`"
        v-html="warningApartmentsAfter"
      />
    </ApplicationSection>

    <!-- Search button -->
    <div
      v-if="interactive && apartmentTypeSearchQueryValue !== 'owner-occupancy'"
      class=""
    >
      <div class="application-apartments__more print-hide">
        <a
          :href="urlApartmentSearch"
          class="btn btn--green-light btn--icon-prefix"
        >
          <SearchIcon />
          {{translate('Hae lisää asuntoja')}}
        </a>
      </div>
    </div>

  </div>
</template>

<script>
  import globals from '../../lib/globals';
  import utils from '../mixins/utils';
  import ApplicationSection from './ApplicationSection';
  import ApplicationItemsGrid from './ApplicationItemsGrid';
  import ApplicationApartmentCard from './ApplicationApartmentCard';
  import ApplicationDivider from './ApplicationDivider';
  import LoadSpinner from './LoadSpinner';
  import PlusIcon from '../icons/PlusIcon.vue';
  import ApartmentsTable from './ApartmentsTable';
  import SearchIcon from '../icons/SearchIcon.vue';

  export default {
    mixins: [
      utils.translate,
    ],
    components: {
      ApplicationSection,
      ApplicationItemsGrid,
      ApplicationApartmentCard,
      ApplicationDivider,
      LoadSpinner,
      PlusIcon,
      SearchIcon,
      ApartmentsTable
    },
    props: {
      interactive: {
        type: Boolean,
        default: false,
      },
      apartmentsCountMax: {
        type: Number,
        required: true,
      },
      apartments: {
        type: Array,
        default: () => [],
      },
      warningApartmentsAfter: {
        type: String,
        default: '',
      },
      loading: {
        type: Boolean,
        required: true,
      },
      color: {
        type: String,
        default: 'default',
      },
      apartmentTypeSearchQueryValue: {
        type: String,
        default: 'rental'
      },
      residentInLocation: {
        type: Object,
        default: {},
      },
    },
    data() {
      return {
        urlApartmentSearch: `${globals.urlApartmentSearch}?q={"type":"${this.apartmentTypeSearchQueryValue}"}`,
      };
    },
    computed: {
      apartmentsAvailable() {
        return this.apartments.filter(apartment => apartment.available);
      },
      apartmentsUnavailable() {
        return this.apartments.filter(apartment => !apartment.available);
      },
      availableApartmentsGroupedByAddress() {
        let addresses = {}

        for (let apartment of this.apartments) {
          if (!apartment.available)
            continue

          addresses[apartment.building.mpc_katuosoite] = []
        }

        for (let apartment of this.apartments) {
          if (!apartment.available)
            continue

          addresses[apartment.building.mpc_katuosoite].push(apartment)
        }

        Object.values(addresses).map(item => {
        return item
          .sort(function(a, b){return a.mpc_vastike-b.mpc_vastike})
          .sort(function(a, b){return a.mpc_pinta_ala-b.mpc_pinta_ala})
        })

        return addresses
      },
      unavailableApartmentsGroupedByAddress() {
        let addresses = {}

        for (let apartment of this.apartments) {
          if (apartment.available)
            continue

          addresses[apartment.building.mpc_katuosoite] = []
        }

        for (let apartment of this.apartments) {
          if (apartment.available)
            continue

          addresses[apartment.building.mpc_katuosoite].push(apartment)
        }

        Object.values(addresses).map(item => {
          return item
          .sort(function(a, b){return a.mpc_vastike-b.mpc_vastike})
          .sort(function(a, b){return a.mpc_pinta_ala-b.mpc_pinta_ala})
        })
        return addresses
      },
      apartmentsGroupedByAddress() {
        let addresses = {}
        let groupedLocalitiesByCity = {}

        for (let apartment of this.apartments) {
          addresses[apartment.housing_locality.mpc_name] = {
            locality_id: apartment.building.mpc_parentkohdeid,
            available : [],
            unavailable: []
          }

          if (typeof groupedLocalitiesByCity[apartment.city.mpc_kuntaid] === 'undefined') {
            groupedLocalitiesByCity[apartment.city.mpc_kuntaid] = [];
          }
          if (!groupedLocalitiesByCity[apartment.city.mpc_kuntaid].includes(apartment.building.mpc_parentkohdeid)) {
            groupedLocalitiesByCity[apartment.city.mpc_kuntaid].push(apartment.building.mpc_parentkohdeid);
          }
        }
        //console.log(groupedLocalitiesByCity);

        for (let apartment of this.apartments) {
          if (apartment.available) {
            addresses[apartment.housing_locality.mpc_name].available.push(apartment)
          }
          if (!apartment.available) {
            addresses[apartment.housing_locality.mpc_name].unavailable.push(apartment)
          }
        }

        Object.values(addresses).map(item => {
          item.available
          .sort(function(a, b){return a.mpc_vastike-b.mpc_vastike})
          .sort(function(a, b){return a.mpc_pinta_ala-b.mpc_pinta_ala})
          item.unavailable
          .sort(function(a, b){return a.mpc_vastike-b.mpc_vastike})
          .sort(function(a, b){return a.mpc_pinta_ala-b.mpc_pinta_ala})
          return item
        })
        this.$emit('groupedLocalitiesByCity', groupedLocalitiesByCity)
        this.$emit('groupedLocalities', Object.keys(addresses).length)
        return addresses
      },
    },
    methods: {
      count(array) {
        return this.loading ? '' : `(${array.length}/${this.apartmentsCountMax})`;
      },

      handleCheckboxChange(checkboxValue, localityId) {
        this.$nextTick(() => {
          if (this.residentInLocation.localityName.includes(checkboxValue)) {
            this.residentInLocation.localityName = this.residentInLocation.localityName.filter(item => item === checkboxValue);
          }
          let localityIdEmit = this.residentInLocation.localityName.length !== 0 ? localityId : "";
          this.$emit('selectedCheckbox', {
            value: JSON.parse(JSON.stringify(this.residentInLocation.localityName)),
            localityId: localityIdEmit,
          });
        })
      },
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .application-apartments__more {
    display: flex;
    justify-content: flex-start;
  }

  .application-apartments__more-link {
    @include font-label-16;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .application-apartments__more-icon {
    @include square(r(40));
    margin-bottom: r(16);
    border-radius: r(20);
    background-color: $color-green-2025;
    color: $color-white;

    svg {
      fill: $white;
    }

    &--pink {
      background-color: $color-pink;
    }

    &--yellow {
      background-color: $color-yellow;
    }
  }

  .apartment-grouped-by-address__container {
    display: flex;
    flex-direction: column;

    .apartment-grouped-by-address--title {
      font-size: 18px;
      line-height: 22px;
      font-weight: bold;
      margin-bottom: 12px;
    }
    .apartment-grouped-by-address--checkbox {
      padding: r(8) 0 r(22);
    }

  }

  .application-apartments__unavailable-apartment {
    background-color: #f0f0f0;
  }
</style>
